import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './Investors.css';

// Translation content
const content = {
    en: {
        hero: {
            headline: 'The Global Future of Boat Rentals',
            subheadline: 'Enterprise-grade marketplace. Multi-platform technology. Worldwide vision.',
            cta: 'Schedule a Meeting',
            ctaSecondary: 'Request Deck PDF'
        },
        problem: {
            title: 'The Problem',
            points: [
                'Global nautical market remains fragmented with no dominant platform',
                'No single solution combines all necessary functionalities for owners, renters, and dealers',
                'Operators depend on manual and fragmented booking methods',
                'GetMyBoat and Boatsetter had to merge to compete - an opportunity to surpass them'
            ]
        },
        solution: {
            title: 'The Solution',
            subtitle: 'NAVEGA is the all-in-one platform for the global boating industry',
            points: [
                'Unified marketplace for owners, renters, and dealers',
                'Integrated payments (Stripe) with 135+ currencies',
                'Identity verification and security',
                'Multi-language coverage (ES, EN, FR, IT, PT, Arabic coming soon)',
                'Combined functionalities that no competitor offers together'
            ]
        },
        market: {
            title: 'Market Opportunity',
            tam: { value: '$25B', label: 'TAM - Global Boat Rental 2025' },
            sam: { value: '$7B', label: 'SAM - Americas + Europe' },
            som: { value: '$500M', label: 'SOM - Caribbean, LATAM, USA' },
            cagr: '5.5-6.5% CAGR projected growth'
        },
        traction: {
            title: 'Traction & Experience',
            points: [
                'Complete platform in production',
                'Native apps on iOS and Android',
                'Operational experience: Black Pearl Marine & Yacht Brokerage LLC',
                'Established network: LanchasVenezuela.com as market base',
                'Functional booking and payment system',
                'Operational Lead Management CRM'
            ]
        },
        business: {
            title: 'Business Model',
            streams: [
                { name: 'Guest Fee', desc: '10-15% per booking (client side)' },
                { name: 'Host Fee', desc: '15-20% per booking (owner side)' },
                { name: 'Featured Listings', desc: 'Premium promotion' },
                { name: 'Dealer SaaS', desc: 'Monthly subscription for brokers' },
                { name: 'Lead Generation', desc: 'Qualified leads sales' },
                { name: 'Insurance Partnership', desc: 'Insurance commissions' }
            ],
            expansion: {
                title: 'Category Expansion (Roadmap)',
                items: [
                    'Beach Lodges: Coastal accommodations linked directly to boat charters',
                    'Marine Experiences: Diving, wakesurf, tours, sport fishing - linked to available boats',
                    'Integrated Packages: Boat + Lodge + Experience as a single offer'
                ]
            }
        },
        competition: {
            title: 'Competitive Advantage',
            subtitle: 'Why NAVEGA will dominate the merged GetMyBoat + Boatsetter',
            mergerNote: 'GetMyBoat + Boatsetter merged December 2025: $500M+ bookings, 50K listings, 13M users',
            comparison: [
                { feature: 'Owner Fees', competitor: '11-35%', navega: '15-20%' },
                { feature: 'Renter Fees', competitor: '13-16%', navega: '10-15%' },
                { feature: 'Currency Conversion', competitor: '3% fee', navega: 'FREE (135+ currencies)' },
                { feature: 'Social Media Auto-Post', competitor: 'None', navega: '6 platforms (X, FB, IG, Threads, Bluesky, GMB)' },
                { feature: 'Crypto Payments', competitor: 'None', navega: '400+ wallets (MetaMask, Coinbase...)' },
                { feature: 'Languages', competitor: '1-2', navega: '5+ native' },
                { feature: 'Categories', competitor: 'Boats only', navega: 'Boats + Experiences + Lodges' }
            ]
        },
        tech: {
            title: 'Technology Edge',
            subtitle: 'Unique capabilities no competitor offers',
            features: [
                {
                    icon: '📱',
                    title: 'Social Media Automation',
                    desc: 'Every listing auto-posts to X, Facebook, Instagram, Threads, Bluesky & Google Business. Zero manual work for owners.'
                },
                {
                    icon: '₿',
                    title: 'Crypto-Ready Payments',
                    desc: '400+ wallet integrations via Stripe. Accept Bitcoin, Ethereum, USDC and more. First mass-market boat rental with crypto.'
                },
                {
                    icon: '📊',
                    title: 'Owner Analytics Dashboard',
                    desc: 'Real-time metrics: views, leads, conversions, revenue. Full CRM for lead management.'
                },
                {
                    icon: '🌍',
                    title: 'True Multi-Language',
                    desc: '5 native languages (ES, EN, FR, IT, PT) + Arabic coming. Deep localization, not just translation.'
                }
            ]
        },
        projections: {
            title: 'Projections',
            note: 'Benchmark: GetMyBoat with ~50K listings achieved $1B+ valuation',
            years: [
                { year: '2026', listings: '50K', revenue: '$2M', valuation: '$100M' },
                { year: '2027', listings: '100K', revenue: '$8M', valuation: '$400M' },
                { year: '2028', listings: '200K', revenue: '$25M', valuation: '$800M' },
                { year: '2029', listings: '400K', revenue: '$60M', valuation: '$1.5B' },
                { year: '2030', listings: '750K+', revenue: '$120M+', valuation: '$2.5B+' }
            ]
        },
        investment: {
            title: 'Investment',
            round: 'Seed Round',
            amount: '$4M USD',
            use: {
                title: 'Use of Funds',
                items: [
                    { percent: '35%', desc: 'Product development (8-10 dev team)' },
                    { percent: '35%', desc: 'Aggressive marketing and acquisition' },
                    { percent: '20%', desc: 'Operations, legal, licenses' },
                    { percent: '10%', desc: 'Strategic reserve' }
                ]
            }
        },
        contact: {
            title: 'Get in Touch',
            subtitle: 'Schedule a meeting to learn more about this opportunity',
            form: {
                name: 'Full Name',
                email: 'Email',
                company: 'Company / Fund',
                message: 'Message',
                submit: 'Send Message'
            }
        },
        product: {
            title: 'Product',
            subtitle: 'Native apps available on iOS and Android',
            screens: ['Splash', 'Explore', 'Listings', 'Map', 'Trips'],
            appStore: 'Download on App Store',
            playStore: 'Get it on Google Play'
        },
        metrics: {
            title: 'Product Metrics',
            subtitle: 'Built for scale from day one',
            items: [
                { value: '3', label: 'Platforms', detail: 'iOS, Android, Web' },
                { value: '5.0', label: 'App Rating', detail: 'iOS App Store' },
                { value: '400+', label: 'Crypto Wallets', detail: 'MetaMask, Coinbase, Trust Wallet...' },
                { value: '135+', label: 'Currencies', detail: 'Stripe Payments' },
                { value: '5', label: 'Languages', detail: 'ES, EN, FR, IT, PT + Arabic soon' },
                { value: '24/7', label: 'Infrastructure', detail: 'Cloudflare CDN + Firebase' }
            ]
        },
        footer: {
            copyright: '2026 NAVEGA. All rights reserved.',
            tagline: 'The Global Future of Boat Rentals'
        }
    },
    es: {
        hero: {
            headline: 'El Futuro Global del Alquiler de Botes',
            subheadline: 'Marketplace de nivel empresarial. Tecnologia multiplataforma. Vision mundial.',
            cta: 'Agendar Reunion',
            ctaSecondary: 'Solicitar Deck PDF'
        },
        problem: {
            title: 'El Problema',
            points: [
                'El mercado nautico global permanece fragmentado sin una plataforma dominante',
                'Ninguna solucion combina todas las funcionalidades necesarias para propietarios, arrendatarios y dealers',
                'Los operadores dependen de metodos manuales y fragmentados de reserva',
                'GetMyBoat y Boatsetter tuvieron que fusionarse para competir - una oportunidad para superarlos'
            ]
        },
        solution: {
            title: 'La Solucion',
            subtitle: 'NAVEGA es la plataforma todo-en-uno para la industria nautica global',
            points: [
                'Marketplace unificado para propietarios, arrendatarios y dealers',
                'Pagos integrados (Stripe) con 135+ monedas',
                'Verificacion de identidad y seguridad',
                'Cobertura multi-idioma (ES, EN, FR, IT, PT, Arabe proximamente)',
                'Funcionalidades combinadas que ningun competidor ofrece juntas'
            ]
        },
        market: {
            title: 'Oportunidad de Mercado',
            tam: { value: '$25B', label: 'TAM - Alquiler Global de Botes 2025' },
            sam: { value: '$7B', label: 'SAM - Americas + Europa' },
            som: { value: '$500M', label: 'SOM - Caribe, LATAM, USA' },
            cagr: 'Crecimiento proyectado de 5.5-6.5% CAGR'
        },
        traction: {
            title: 'Traccion y Experiencia',
            points: [
                'Plataforma completa en produccion',
                'Apps nativas en iOS y Android',
                'Experiencia operativa: Black Pearl Marine & Yacht Brokerage LLC',
                'Red establecida: LanchasVenezuela.com como base de mercado',
                'Sistema de reservas y pagos funcional',
                'CRM de Gestion de Leads operativo'
            ]
        },
        business: {
            title: 'Modelo de Negocio',
            streams: [
                { name: 'Comision Cliente', desc: '10-15% por reserva (lado del cliente)' },
                { name: 'Comision Host', desc: '15-20% por reserva (lado del propietario)' },
                { name: 'Listings Destacados', desc: 'Promocion premium' },
                { name: 'Dealer SaaS', desc: 'Suscripcion mensual para brokers' },
                { name: 'Generacion de Leads', desc: 'Venta de leads cualificados' },
                { name: 'Alianza de Seguros', desc: 'Comisiones de seguros' }
            ],
            expansion: {
                title: 'Expansion de Categorias (Roadmap)',
                items: [
                    'Posadas Playeras: Alojamientos costeros vinculados directamente con charters de botes',
                    'Experiencias Marinas: Buceo, wakesurf, paseos, pesca deportiva - vinculadas a los botes',
                    'Paquetes Integrados: Bote + Posada + Experiencia como oferta unica'
                ]
            }
        },
        competition: {
            title: 'Ventaja Competitiva',
            subtitle: 'Por que NAVEGA dominara sobre la fusion GetMyBoat + Boatsetter',
            mergerNote: 'GetMyBoat + Boatsetter fusionados Diciembre 2025: $500M+ reservas, 50K listings, 13M usuarios',
            comparison: [
                { feature: 'Comision Owner', competitor: '11-35%', navega: '15-20%' },
                { feature: 'Comision Renter', competitor: '13-16%', navega: '10-15%' },
                { feature: 'Conversion Moneda', competitor: '3% fee', navega: 'GRATIS (135+ monedas)' },
                { feature: 'Auto-Post Redes Sociales', competitor: 'Ninguno', navega: '6 plataformas (X, FB, IG, Threads, Bluesky, GMB)' },
                { feature: 'Pagos Crypto', competitor: 'Ninguno', navega: '400+ wallets (MetaMask, Coinbase...)' },
                { feature: 'Idiomas', competitor: '1-2', navega: '5+ nativos' },
                { feature: 'Categorias', competitor: 'Solo botes', navega: 'Botes + Experiencias + Posadas' }
            ]
        },
        tech: {
            title: 'Ventaja Tecnologica',
            subtitle: 'Capacidades unicas que ningun competidor ofrece',
            features: [
                {
                    icon: '📱',
                    title: 'Automatizacion Redes Sociales',
                    desc: 'Cada listing se publica automaticamente en X, Facebook, Instagram, Threads, Bluesky y Google Business. Cero trabajo manual para propietarios.'
                },
                {
                    icon: '₿',
                    title: 'Pagos Crypto-Ready',
                    desc: '400+ integraciones de wallets via Stripe. Acepta Bitcoin, Ethereum, USDC y mas. Primer rental de botes masivo con crypto.'
                },
                {
                    icon: '📊',
                    title: 'Dashboard Analytics para Owners',
                    desc: 'Metricas en tiempo real: vistas, leads, conversiones, revenue. CRM completo para gestion de leads.'
                },
                {
                    icon: '🌍',
                    title: 'Multi-Idioma Real',
                    desc: '5 idiomas nativos (ES, EN, FR, IT, PT) + Arabe pronto. Localizacion profunda, no solo traduccion.'
                }
            ]
        },
        projections: {
            title: 'Proyecciones',
            note: 'Benchmark: GetMyBoat con ~50K listings alcanzo valoracion de $1B+',
            years: [
                { year: '2026', listings: '50K', revenue: '$2M', valuation: '$100M' },
                { year: '2027', listings: '100K', revenue: '$8M', valuation: '$400M' },
                { year: '2028', listings: '200K', revenue: '$25M', valuation: '$800M' },
                { year: '2029', listings: '400K', revenue: '$60M', valuation: '$1.5B' },
                { year: '2030', listings: '750K+', revenue: '$120M+', valuation: '$2.5B+' }
            ]
        },
        investment: {
            title: 'Inversion',
            round: 'Ronda Seed',
            amount: '$4M USD',
            use: {
                title: 'Uso de Fondos',
                items: [
                    { percent: '35%', desc: 'Desarrollo de producto (equipo de 8-10 devs)' },
                    { percent: '35%', desc: 'Marketing y adquisicion agresiva' },
                    { percent: '20%', desc: 'Operaciones, legal, licencias' },
                    { percent: '10%', desc: 'Reserva estrategica' }
                ]
            }
        },
        contact: {
            title: 'Contactanos',
            subtitle: 'Agenda una reunion para conocer mas sobre esta oportunidad',
            form: {
                name: 'Nombre Completo',
                email: 'Correo Electronico',
                company: 'Empresa / Fondo',
                message: 'Mensaje',
                submit: 'Enviar Mensaje'
            }
        },
        product: {
            title: 'Producto',
            subtitle: 'Apps nativas disponibles en iOS y Android',
            screens: ['Inicio', 'Explorar', 'Listados', 'Mapa', 'Viajes'],
            appStore: 'Descargar en App Store',
            playStore: 'Disponible en Google Play'
        },
        metrics: {
            title: 'Metricas de Producto',
            subtitle: 'Construido para escalar desde el dia uno',
            items: [
                { value: '3', label: 'Plataformas', detail: 'iOS, Android, Web' },
                { value: '5.0', label: 'Rating App', detail: 'iOS App Store' },
                { value: '400+', label: 'Wallets Crypto', detail: 'MetaMask, Coinbase, Trust Wallet...' },
                { value: '135+', label: 'Monedas', detail: 'Pagos Stripe' },
                { value: '5', label: 'Idiomas', detail: 'ES, EN, FR, IT, PT + Arabe pronto' },
                { value: '24/7', label: 'Infraestructura', detail: 'Cloudflare CDN + Firebase' }
            ]
        },
        footer: {
            copyright: '2026 NAVEGA. Todos los derechos reservados.',
            tagline: 'El Futuro Global del Alquiler de Botes'
        }
    }
};

class Investors extends React.Component {
    static propTypes = {
        locale: PropTypes.string
    };

    static defaultProps = {
        locale: 'en'
    };

    constructor(props) {
        super(props);
        const lang = props.locale && props.locale.startsWith('es') ? 'es' : 'en';
        this.state = {
            lang,
            formData: { name: '', email: '', company: '', message: '' },
            formSubmitting: false,
            formSuccess: false,
            formError: ''
        };
    }

    toggleLanguage = () => {
        this.setState(prev => ({ lang: prev.lang === 'en' ? 'es' : 'en' }));
    };

    handleInputChange = (field, value) => {
        this.setState(prev => ({
            formData: { ...prev.formData, [field]: value }
        }));
    };

    handleSubmit = async (e) => {
        e.preventDefault();
        this.setState({ formSubmitting: true, formError: '' });

        // Simulate form submission
        setTimeout(() => {
            this.setState({ formSubmitting: false, formSuccess: true });
        }, 1500);
    };

    render() {
        const { lang, formData, formSubmitting, formSuccess, formError } = this.state;
        const t = content[lang];

        return (
            <div className={s.container}>
                {/* Language Selector */}
                <div className={s.langSelector}>
                    <button
                        onClick={this.toggleLanguage}
                        className={s.langButton}
                    >
                        {lang === 'en' ? 'ES' : 'EN'}
                    </button>
                </div>

                {/* Hero Section */}
                <section className={s.hero}>
                    <div className={s.heroContent}>
                        <div className={s.logo}>
                            <img
                                src="/navega-logo-white.png"
                                alt="NAVEGA"
                                className={s.logoImage}
                            />
                        </div>
                        <h1 className={s.headline}>{t.hero.headline}</h1>
                        <p className={s.subheadline}>{t.hero.subheadline}</p>
                        <div className={s.ctaGroup}>
                            <a href="#contact" className={s.ctaPrimary}>{t.hero.cta}</a>
                            <a href="#contact" className={s.ctaSecondary}>{t.hero.ctaSecondary}</a>
                        </div>
                    </div>
                </section>

                {/* Problem Section */}
                <section className={s.section}>
                    <h2 className={s.sectionTitle}>{t.problem.title}</h2>
                    <ul className={s.pointsList}>
                        {t.problem.points.map((point, i) => (
                            <li key={i} className={s.point}>{point}</li>
                        ))}
                    </ul>
                </section>

                {/* Solution Section */}
                <section className={s.sectionAlt}>
                    <h2 className={s.sectionTitle}>{t.solution.title}</h2>
                    <p className={s.subtitle}>{t.solution.subtitle}</p>
                    <ul className={s.pointsList}>
                        {t.solution.points.map((point, i) => (
                            <li key={i} className={s.point}>{point}</li>
                        ))}
                    </ul>
                </section>

                {/* Product Showcase Section */}
                <section className={s.section}>
                    <h2 className={s.sectionTitle}>{t.product.title}</h2>
                    <p className={s.subtitle}>{t.product.subtitle}</p>
                    <div className={s.screenshotCarousel}>
                        <div className={s.screenshotTrack}>
                            <img src="/investors/app-splash.png" alt={t.product.screens[0]} className={s.screenshot} />
                            <img src="/investors/app-explore.png" alt={t.product.screens[1]} className={s.screenshot} />
                            <img src="/investors/app-listings.png" alt={t.product.screens[2]} className={s.screenshot} />
                            <img src="/investors/app-map.png" alt={t.product.screens[3]} className={s.screenshot} />
                            <img src="/investors/app-trips.png" alt={t.product.screens[4]} className={s.screenshot} />
                        </div>
                    </div>
                    <div className={s.appBadges}>
                        <a href="https://apps.apple.com/us/app/navega-renta-tu-bote/id6742487553" target="_blank" rel="noopener noreferrer" className={s.appBadge}>
                            <svg viewBox="0 0 120 40" className={s.badgeSvg}>
                                <rect width="120" height="40" rx="6" fill="#000" />
                                <text x="60" y="16" textAnchor="middle" fill="#fff" fontSize="8">Download on the</text>
                                <text x="60" y="28" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="600">App Store</text>
                            </svg>
                        </a>
                        <a href="https://play.google.com/store/apps/details?id=com.navega.app" target="_blank" rel="noopener noreferrer" className={s.appBadge}>
                            <svg viewBox="0 0 135 40" className={s.badgeSvg}>
                                <rect width="135" height="40" rx="6" fill="#000" />
                                <text x="68" y="16" textAnchor="middle" fill="#fff" fontSize="8">GET IT ON</text>
                                <text x="68" y="28" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="600">Google Play</text>
                            </svg>
                        </a>
                    </div>
                </section>

                {/* Product Metrics Section */}
                <section className={s.sectionAlt}>
                    <h2 className={s.sectionTitle}>{t.metrics.title}</h2>
                    <p className={s.subtitle}>{t.metrics.subtitle}</p>
                    <div className={s.metricsGrid}>
                        {t.metrics.items.map((item, i) => (
                            <div key={i} className={s.metricCard}>
                                <div className={s.metricValue}>{item.value}</div>
                                <div className={s.metricLabel}>{item.label}</div>
                                <div className={s.metricDetail}>{item.detail}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Market Section */}
                <section className={s.section}>
                    <h2 className={s.sectionTitle}>{t.market.title}</h2>
                    <div className={s.marketGrid}>
                        <div className={s.marketCard}>
                            <div className={s.marketValue}>{t.market.tam.value}</div>
                            <div className={s.marketLabel}>{t.market.tam.label}</div>
                        </div>
                        <div className={s.marketCard}>
                            <div className={s.marketValue}>{t.market.sam.value}</div>
                            <div className={s.marketLabel}>{t.market.sam.label}</div>
                        </div>
                        <div className={s.marketCard}>
                            <div className={s.marketValue}>{t.market.som.value}</div>
                            <div className={s.marketLabel}>{t.market.som.label}</div>
                        </div>
                    </div>
                    <p className={s.cagr}>{t.market.cagr}</p>
                </section>

                {/* Traction Section */}
                <section className={s.sectionAlt}>
                    <h2 className={s.sectionTitle}>{t.traction.title}</h2>
                    <ul className={s.pointsList}>
                        {t.traction.points.map((point, i) => (
                            <li key={i} className={s.point}>{point}</li>
                        ))}
                    </ul>
                </section>

                {/* Business Model Section */}
                <section className={s.section}>
                    <h2 className={s.sectionTitle}>{t.business.title}</h2>
                    <div className={s.businessGrid}>
                        {t.business.streams.map((stream, i) => (
                            <div key={i} className={s.businessCard}>
                                <div className={s.businessName}>{stream.name}</div>
                                <div className={s.businessDesc}>{stream.desc}</div>
                            </div>
                        ))}
                    </div>
                    <div className={s.expansion}>
                        <h3 className={s.expansionTitle}>{t.business.expansion.title}</h3>
                        <ul className={s.expansionList}>
                            {t.business.expansion.items.map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* Competition Section - Comparison Table */}
                <section className={s.sectionAlt}>
                    <h2 className={s.sectionTitle}>{t.competition.title}</h2>
                    <p className={s.subtitle}>{t.competition.subtitle}</p>
                    <p className={s.mergerNote}>{t.competition.mergerNote}</p>
                    <div className={s.comparisonTable}>
                        <div className={s.comparisonHeader}>
                            <div>Feature</div>
                            <div>GetMyBoat + Boatsetter</div>
                            <div className={s.navegaCol}>NAVEGA</div>
                        </div>
                        {t.competition.comparison.map((row, i) => (
                            <div key={i} className={s.comparisonRow}>
                                <div className={s.featureCol}>{row.feature}</div>
                                <div className={s.competitorCol}>{row.competitor}</div>
                                <div className={s.navegaCol}>{row.navega}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Technology Edge Section */}
                <section className={s.section}>
                    <h2 className={s.sectionTitle}>{t.tech.title}</h2>
                    <p className={s.subtitle}>{t.tech.subtitle}</p>
                    <div className={s.techGrid}>
                        {t.tech.features.map((feature, i) => (
                            <div key={i} className={s.techCard}>
                                <div className={s.techIcon}>{feature.icon}</div>
                                <h3 className={s.techTitle}>{feature.title}</h3>
                                <p className={s.techDesc}>{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Projections Section */}
                <section className={s.section}>
                    <h2 className={s.sectionTitle}>{t.projections.title}</h2>
                    <div className={s.projectionsTable}>
                        <div className={s.projHeader}>
                            <div>Year</div>
                            <div>Listings</div>
                            <div>Revenue</div>
                            <div>Valuation</div>
                        </div>
                        {t.projections.years.map((year, i) => (
                            <div key={i} className={s.projRow}>
                                <div className={s.projYear}>{year.year}</div>
                                <div>{year.listings}</div>
                                <div>{year.revenue}</div>
                                <div className={s.projVal}>{year.valuation}</div>
                            </div>
                        ))}
                    </div>
                    <p className={s.benchmark}>{t.projections.note}</p>
                </section>

                {/* Investment Section */}
                <section className={s.sectionAlt}>
                    <h2 className={s.sectionTitle}>{t.investment.title}</h2>
                    <div className={s.investmentInfo}>
                        <div className={s.investRound}>{t.investment.round}</div>
                        <div className={s.investAmount}>{t.investment.amount}</div>
                    </div>
                    <h3 className={s.useTitle}>{t.investment.use.title}</h3>
                    <div className={s.useGrid}>
                        {t.investment.use.items.map((item, i) => (
                            <div key={i} className={s.useCard}>
                                <div className={s.usePercent}>{item.percent}</div>
                                <div className={s.useDesc}>{item.desc}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Contact Section */}
                <section id="contact" className={s.contactSection}>
                    <h2 className={s.sectionTitle}>{t.contact.title}</h2>
                    <p className={s.subtitle}>{t.contact.subtitle}</p>

                    {formSuccess ? (
                        <div className={s.successMessage}>
                            <div className={s.successIcon}>&#10003;</div>
                            <p>{lang === 'en' ? 'Message sent successfully. We will contact you soon.' : 'Mensaje enviado exitosamente. Te contactaremos pronto.'}</p>
                        </div>
                    ) : (
                        <form onSubmit={this.handleSubmit} className={s.contactForm}>
                            <input
                                type="text"
                                placeholder={t.contact.form.name}
                                value={formData.name}
                                onChange={(e) => this.handleInputChange('name', e.target.value)}
                                required
                                className={s.input}
                            />
                            <input
                                type="email"
                                placeholder={t.contact.form.email}
                                value={formData.email}
                                onChange={(e) => this.handleInputChange('email', e.target.value)}
                                required
                                className={s.input}
                            />
                            <input
                                type="text"
                                placeholder={t.contact.form.company}
                                value={formData.company}
                                onChange={(e) => this.handleInputChange('company', e.target.value)}
                                className={s.input}
                            />
                            <textarea
                                placeholder={t.contact.form.message}
                                value={formData.message}
                                onChange={(e) => this.handleInputChange('message', e.target.value)}
                                rows={4}
                                className={s.textarea}
                            />
                            {formError && <p className={s.error}>{formError}</p>}
                            <button type="submit" disabled={formSubmitting} className={s.submitButton}>
                                {formSubmitting ? '...' : t.contact.form.submit}
                            </button>
                        </form>
                    )}
                </section>

                {/* Footer */}
                <footer className={s.footer}>
                    <p className={s.footerTagline}>{t.footer.tagline}</p>
                    <p className={s.copyright}>{t.footer.copyright}</p>
                </footer>
            </div>
        );
    }
}

export default withStyles(s)(Investors);
